<script>
  import {comparisonForStrings, getLogoFromAccount} from 'root/services/tools';
  import Logo from './AccountManageCompaniesLogo.svelte';
  import {onDestroy} from 'svelte';
  import manageCompaniesService from 'vRoot/account-profile/modules/manage-companies/services/manage-company.service';
  import {popoverApiSupport} from 'utils/html-support-service';
  import RbDropdownActions from 'rfp/rfp-manager/main/pages/components/RfpManagerDropdownActions.svelte';

  export let
    state,
    account,
    userAccount,
    isAdmin,
    scrollContainerElement,
    refreshCompanies = () => {};

  const ACTIONS = [
    {
      id: 'primary',
      icon: 'perm_contact_calendar',
      label: 'Assign To',
      action: () => {
        manageCompaniesService.manageAssignments(userAccount.accountId, account).then(refreshCompanies);
      },
      available: () => isAdmin,
    },
    {
      id: 'edit',
      icon: 'edit',
      label: 'Company Information',
      action: () => {
        if(isCurrentAccount){
          manageCompaniesService.openCompanyProfile(account.id, state);
        } else {
          manageCompaniesService.editMyCompanyProfile();
        }
      }
    },
    {
      id: 'contact',
      icon: 'person',
      label: 'Edit Primary Contact',
      action: () => {
        manageCompaniesService.editPrimaryContact(account).then(refreshCompanies);
      }
    },
    {
      id: 'logo',
      icon: 'image',
      label: 'Change Logo',
      action: () => {
        manageCompaniesService.editLogo(account).then(refreshCompanies);
      }
    },
    {
      id: 'rateLoading',
      icon: 'rate_review',
      label: 'Rate Loading Information',
      action: () => {
        manageCompaniesService.manageRateLoadingInformation(account.entityId);
      }
    },
    {
      id: 'directory',
      icon: 'list',
      label: 'View Hotel Directory',
      action: () => {
        manageCompaniesService.openHotelDirectory(account.id)
      }
    },
    {
      id: 'delete',
      icon: 'delete',
      label: 'Delete Company',
      action: () => {
        manageCompaniesService.deleteTarget(userAccount.accountId, account).then(refreshCompanies)
      },
      available: () => !isCurrentAccount && isAdmin,
    }
  ];
  const popoverApiSupported = popoverApiSupport();

  let
    isCurrentAccount,
    isAssignedToCurrentUser,
    managersText,
    contact,
    menuElement;

  $: isCurrentAccount = account.id === userAccount.accountId;
  $: isAssignedToCurrentUser = (account.managers || []).filter(m => m.userId === userAccount.id).length;
  $: managersText = (account.managers || []).map(m => m.userDetails.fullName).sort(comparisonForStrings).join(', ');
  $: contact = account.primaryContact;
  $: if(scrollContainerElement) scrollContainerElement.addEventListener('scroll', handleScroll);

  onDestroy(() => {
    if(scrollContainerElement) scrollContainerElement.removeEventListener('scroll', handleScroll);
  })

  function handleScroll(){
    if(menuElement) menuElement.hidePopover();
  }

</script>

<div class="ManageAccountsRow">
  <Logo logo="{getLogoFromAccount(account)}" />

  <div class="Details">
    <div class="Name">
      {account.name}
    </div>

    {#if account.managers && account.managers.length}
      {#if account.managers.length > 4}
        <div class="Companies">
          Assigned To <b>{account.managers.length} managers</b>
          <div class="rbTooltip simple ManagersTooltip" data-tooltip="placement:top-start">
            <div>Account Managers:</div>
            <div>{managersText}</div>
          </div>
        </div>
      {:else}
        <div class="Companies">
          Assigned To <b>{managersText}</b>
        </div>
      {/if}
    {/if}

    {#if contact}
      <div>
        {#if contact.emailAddress}<span>{contact.emailAddress}</span>{/if}
        {#if contact.emailAddress && contact.phone}<span class="Dot">&middot;</span>{/if}
        {#if contact.phone}<span>{ contact.phone }</span>{/if}
      </div>
    {/if}
  </div>


  {#if isAdmin || isAssignedToCurrentUser}
    <div class="Actions">
      {#if popoverApiSupported}
        <button class="ActionButton"
                popovertarget="{account.id}"
                data-icon-before="settings"
                data-icon-after="arrow_drop_down"
        >Manage</button>

        <div class="Menu"
             bind:this={menuElement}
             id="{account.id}"
             popover
        >
          {#each ACTIONS.filter(a => !a.available || a.available()) as option}
            <div class="MenuItem"
                 data-icon-before="{option.icon}"
                 on:click={() => {
                 menuElement.hidePopover();
                 option.action();
                }}
            >
              {option.label}
            </div>
          {/each}
        </div>
      {:else}
        <div>
          <RbDropdownActions items="{ACTIONS.filter(a => !a.available || a.available())}"
                             on:input={ev => ev.detail.action()}
                             mode="Light"
                             valueIcon="settings"
                             align='bottom-end'
          >
            <div slot="value">Manage</div>
          </RbDropdownActions>
        </div>

      {/if}
    </div>
  {/if}
</div>

<style lang="stylus">

  .ManageAccountsRow {
    padding 10px 20px;
    border-bottom: 1px solid #ECEFF1;
    height 100px;
    box-sizing border-box;
    display: flex;
    align-items: center;
  }

  .Details {
    flex: 1 1 100%;
    font-size: 12px;
    color: #8a999f;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .Name {
    font-size: 16px;
    color: #37474f;
  }

  .Dot {
    font-size: 15px;
    font-weight: bold;
  }

  .Actions {
    display: flex;
    align-items: center;
  }


  .ActionButton {
    background white linear-gradient(0deg, #f5f6f7 10%, white 11.05%, white 100%);
    height: 27px;
    box-sizing: border-box;
    font-size: 12px;
    padding: 0 5px;
    border-radius: 2px;
    white-space: nowrap;
    line-height: 16px;
    display: flex;
    gap: 5px;
    color: #546e7a;

    &:before, &:after {
      height: 20px;
      font-size: 20px;
      margin: 0;
    }
  }

  .Menu {
    position: absolute;
    position-area: bottom span-left;
    position-try-fallbacks: top span-left;
    margin: unset;

    min-width: 179px;
    width: max-content;
    box-shadow: rgba(0,0,0,0.5) 0 1px 5px 0;
    margin-top: 5px;
    border-radius: 3px;
    font-size: 12px;
    max-height: 300px;
    overflow: auto;

    color: #546e7a;
    background: #fff;
    border: 1px solid #b0bec5;
  }

  .MenuItem {
    padding: 5px;
    cursor: pointer;
    transition: background .3s;
    display: flex;
    gap: 10px;
    align-items: center;
    border-bottom: 1px solid #e5e6e8;

    &:before {
      font-size: 20px;
    }

    &:hover {
      background: #eceff1;
      color: #546e7a;
    }
  }

  .ManagersTooltip {
    max-width: 500px;
    text-align: left;

    div:first-child {
      font-weight: bold;
    }
  }

</style>
